import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import { usePrevious } from "../../utils/utils";

const InputTextOutlined = ({
  id,
  type,
  label,
  tooltip,
  placeholder,
  error,
  initialValue,
  onChangeInput,
  size,
  multiline,
  rowsMultiline,
  endAdornment,
  startAdornment,
  styleControl,
  styleLabel,
  styleInput,
  styleError,
  inputProps,
  isDisabled,
  removeBlankSpaces = false,
}) => {
  const [value, setValue] = useState("");
  const [valueDebouce] = useDebounce(value, 300);
  let inputRef = useRef();
  const prevValue = usePrevious(valueDebouce);

  useEffect(() => {
    if (typeof prevValue !== "undefined") {
      const obj = {
        target: { name: inputRef.current.name, value: inputRef.current.value },
      };
      if (
        (initialValue === value && prevValue === "" && value !== "") ||
        (prevValue !== "" && initialValue === "")
      ) {
        // select simple - prevent re render on capture initial value from request
        return;
      }
      onChangeInput(obj);
    }
  }, [valueDebouce]); // eslint-disable-line

  useMemo(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <>
      <FormControl
        fullWidth
        sx={{ textAlign: "left", ...styleControl }}
        disabled={isDisabled}
      >
        {label && label !== "" && (
          <FormLabel
            error={!!error && error !== ""}
            htmlFor={id}
            sx={{ marginTop: "auto", ...styleLabel }}
          >
            {label} {tooltip}
          </FormLabel>
        )}
        <OutlinedInput
          inputRef={inputRef}
          fullWidth
          name={id}
          id={id}
          value={value}
          onChange={(e) => {
            if (removeBlankSpaces) {
              const fixedText = e.target.value.replaceAll(" ", "");
              setValue(fixedText);
            } else {
              setValue(e.target.value);
            }
          }}
          size={size}
          type={type || "text"}
          multiline={multiline || false}
          rows={rowsMultiline || 1}
          error={!!error}
          placeholder={placeholder}
          disabled={isDisabled || false}
          startAdornment={startAdornment}
          endAdornment={endAdornment}
          sx={{ ...styleInput }}
          inputProps={inputProps}
        />
        {error && (
          <FormHelperText error id={id} sx={{ styleError }}>
            {error}
          </FormHelperText>
        )}
      </FormControl>
    </>
  );
};

InputTextOutlined.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string,
  label: PropTypes.string,
  tooltip: PropTypes.element,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChangeInput: PropTypes.func,
  size: PropTypes.string,
  multiline: PropTypes.bool,
  rowsMultiline: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  startAdornment: PropTypes.element,
  endAdornment: PropTypes.element,
  styleLabel: PropTypes.object,
  styleInput: PropTypes.object,
  styleError: PropTypes.object,
  isDisabled: PropTypes.bool,
  styleControl: PropTypes.object,
  inputProps: PropTypes.object,
};

export default InputTextOutlined;
